import { Link } from "react-router-dom";
import {
  PencilRuler, Blocks, ShieldCheck, Timer, PiggyBank, Handshake,
  ArrowRight, MapPin, Quote, CheckCircle2,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { ADVANTAGES, PRODUCTS, SERVICES, PROCESS_STEPS, EXTERIORS, INTERIORS, TESTIMONIAL, SITE } from "@/data/site";

const ICONS = { PencilRuler, Blocks, ShieldCheck, Timer, PiggyBank, Handshake };

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden bg-[#001529]">
        <img
          src="/images/hero-main.jpg"
          alt="Modern modular building by Vision Building Systems"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/40" />
        <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
        <div className="relative hero-grain max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 lg:pt-10 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-3">
              Vision Building Systems, L.L.C.
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-3xl">
              Temporary & Permanent Buildings,{" "}
              <span className="text-amber-500">Built Modular.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              {SITE.tagline} — sale, delivery, and installation across the Southeastern United States,
              from small and simple to large and complex.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/gallery"
                data-testid="hero-gallery-button"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Explore Our Buildings <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Get a Quote
              </Link>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <dl className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl">
              {[
                ["30+", "Years of industry experience"],
                ["2x", "Faster than conventional construction"],
                ["7+", "Southeastern states served"],
              ].map(([stat, label]) => (
                <div key={label} className="border-l-2 border-amber-500 pl-4">
                  <dt className="font-heading text-3xl font-extrabold text-white">{stat}</dt>
                  <dd className="text-sm text-slate-300 mt-1">{label}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* ---------- VBS Advantage ---------- */}
      <section className="pt-8 pb-16 sm:pt-10 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="advantage-heading">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">The VBS Advantage</p>
            <h2 id="advantage-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              A customer driven modular building partner
            </h2>
            <div className="mt-8 space-y-4 text-base text-slate-600 leading-relaxed">
              <p>
                Vision Building Systems, L.L.C. (VBS) is a Florida limited liability company serving the
                Southeastern United States in the sale, delivery, and installation of modular buildings. We are
                dedicated to providing our clients with extensive technical knowledge and sales experience in all
                facets of the modular building industry.
              </p>
              <p>
                Our innovative management team is committed to satisfying your unique construction and space
                needs. VBS' areas of expertise cover all modular applications including commercial, religious,
                educational, hotel and health care.
              </p>
              <p>
                VBS offers full project services — design, budget proposal, turn-key construction, and project
                management — with customized financial alternatives to meet each client's specific business needs.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="advantage-about-link"
              className="mt-8 inline-flex items-center gap-2 font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200"
            >
              Meet our team <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/ext-sales-hardilap.jpg" alt="Sales center modular building with shingle mansard" className="rounded-xl object-cover h-56 w-full shadow-lg" />
              <img src="/images/adv-1.jpg" alt="Custom modular building exterior" className="rounded-xl object-cover h-56 w-full shadow-lg mt-8" />
              <img src="/images/ext-exec-stucco.jpg" alt="Executive office with stucco exterior and metal mansard" className="rounded-xl object-cover h-56 w-full shadow-lg -mt-8" />
              <img src="/images/ext-clubhouse-crane.jpg" alt="Club house crane set with stucco exterior" className="rounded-xl object-cover h-56 w-full shadow-lg" />
            </div>
            <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-slate-500">
              <MapPin className="h-4 w-4 text-amber-600" aria-hidden="true" />
              {SITE.serviceArea}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Advantages grid ---------- */}
      <section className="bg-white border-y border-slate-100" aria-labelledby="why-modular-heading">
        <div className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Why Modular</p>
            <h2 id="why-modular-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              Engineered advantages, built in
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ADVANTAGES.map((a, i) => {
              const Icon = ICONS[a.icon];
              return (
                <Reveal key={a.title} delay={i * 80}>
                  <article
                    data-testid={`advantage-card-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group h-full bg-slate-50 hover:bg-[#00203d] rounded-xl p-6 sm:p-8 transition-colors duration-300 border border-slate-100"
                  >
                    <div className="h-12 w-12 rounded-lg bg-[#00203d] group-hover:bg-amber-600 flex items-center justify-center transition-colors duration-300">
                      <Icon className="h-6 w-6 text-amber-500 group-hover:text-white transition-colors duration-300" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 font-heading text-lg sm:text-xl font-bold text-slate-900 group-hover:text-white transition-colors duration-300">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 group-hover:text-slate-300 leading-relaxed transition-colors duration-300">
                      {a.text}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Products & Services ---------- */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="products-services-heading">
        <Reveal className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">What We Deliver</p>
          <h2 id="products-services-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
            Products & turn-key services
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <article data-testid="home-products-card" className="overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300">
              <img src="/images/ext-office-ground.jpg" alt="Office building ground set to match existing building" className="h-64 w-full object-cover" />
              <div className="p-6 sm:p-8">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">VBS Products</h3>
                <p className="mt-2 text-sm text-slate-600">
                  From basic offices to complex multi-story buildings, VBS provides solutions for any of your space needs.
                </p>
                <ul className="mt-5 space-y-2.5">
                  {PRODUCTS.map((p) => (
                    <li key={p} className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" /> {p}
                    </li>
                  ))}
                </ul>
                <Link to="/products" data-testid="home-products-link" className="mt-6 inline-flex items-center gap-2 font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200">
                  View products <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </Reveal>
          <Reveal delay={120}>
            <article data-testid="home-services-card" className="overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300">
              <img src="/images/ext-exec-stucco.jpg" alt="Executive office ground set stucco exterior" className="h-64 w-full object-cover" />
              <div className="p-6 sm:p-8">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">VBS Services</h3>
                <p className="mt-2 text-sm text-slate-600">VBS offers turn-key services:</p>
                <ul className="mt-5 space-y-2.5">
                  {SERVICES.map((s) => (
                    <li key={s} className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" /> {s}
                    </li>
                  ))}
                </ul>
                <Link to="/services" data-testid="home-services-link" className="mt-6 inline-flex items-center gap-2 font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200">
                  View services <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      {/* ---------- Ground Set Process ---------- */}
      <section className="bg-[#00203d] blueprint-grid" aria-labelledby="process-heading">
        <div className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">Ground Set Process</p>
            <h2 id="process-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Efficient and seamless, from site prep to turn-key delivery
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
              Ground Set buildings are a great option. The process is efficient and seamless with our team of
              expert installers.
            </p>
          </Reveal>
          <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 90}>
                <li data-testid={`process-step-${s.step}`} className="group">
                  <div className="overflow-hidden rounded-lg">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-4 flex items-start gap-3">
                    <span className="font-heading text-2xl font-extrabold text-amber-500 leading-none">
                      {String(s.step).padStart(2, "0")}
                    </span>
                    <p className="text-sm font-semibold text-white leading-snug">{s.title}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={200}>
            <Link
              to="/services"
              data-testid="process-services-link"
              className="mt-10 inline-flex items-center gap-2 border-2 border-white/30 hover:border-amber-500 text-white font-bold px-6 py-3 rounded-md transition-colors duration-200"
            >
              See how we build <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- Gallery teaser ---------- */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="gallery-teaser-heading">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Our Work</p>
            <h2 id="gallery-teaser-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              Recent modular projects
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="gallery-teaser-link"
            className="inline-flex items-center gap-2 bg-[#00203d] hover:bg-[#001529] text-white font-bold px-6 py-3 rounded-md transition-colors duration-200"
          >
            View full gallery <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[EXTERIORS[5], EXTERIORS[7], INTERIORS[14], EXTERIORS[19]].map((item, i) => (
            <Reveal key={item.img} delay={i * 80}>
              <Link to="/gallery" className="group block overflow-hidden rounded-xl relative" tabIndex={-1}>
                <img
                  src={item.img}
                  alt={item.caption}
                  loading="lazy"
                  className="h-52 lg:h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#001529]/90 to-transparent px-4 pb-3 pt-10 text-xs font-semibold text-white">
                  {item.caption}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Testimonial ---------- */}
      <section className="bg-white border-y border-slate-100" aria-labelledby="testimonial-heading">
        <div className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <Reveal>
            <h2 id="testimonial-heading" className="sr-only">Happy Clients — Our number 1 priority</h2>
            <figure data-testid="home-testimonial" className="text-center">
              <Quote className="h-10 w-10 text-amber-500 mx-auto" aria-hidden="true" />
              <blockquote className="mt-6 font-heading text-xl sm:text-2xl font-semibold text-slate-800 leading-relaxed">
                "{TESTIMONIAL.quote}"
              </blockquote>
              <figcaption className="mt-6">
                <p className="font-bold text-[#00203d]">{TESTIMONIAL.author}</p>
                <p className="text-sm text-slate-500">Happy Clients — our number 1 priority</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="relative overflow-hidden" aria-labelledby="cta-heading">
        <img src="/images/cta-bg.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#001529]/80" />
        <div className="relative hero-grain py-20 sm:py-28 px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 id="cta-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              The Perfect Modular
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-amber-500 font-heading font-semibold">Built just for you</p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="mt-9 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-8 py-4 rounded-md transition-colors duration-200"
            >
              Start your project <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
